"use client";

import Icon from "@/components/ui/AppIcon";
import { motion } from "framer-motion";


const contactInfo = [
  {
    icon: "MapPinIcon",
    label: "Warehouse Address",
    value: "Longhua District, Shenzhen",
    sub: "Guangdong Province, China 518109",
    color: "text-link-purple",
    bg: "bg-deep-purple/20",
  },
  {
    icon: "EnvelopeIcon",
    label: "Email Us",
    value: "hello@heliumfulfilment.com",
    sub: "Response within 2 hours",
    color: "text-blue-400",
    bg: "bg-blue-500/20",
  },
  {
    icon: "ClockIcon",
    label: "Business Hours",
    value: "Mon–Fri, 9AM–6PM CET",
    sub: "Emergency support 24/7 for active clients",
    color: "text-green-400",
    bg: "bg-green-500/20",
  },
];

const quickFaqs = [
  {
    q: "How fast do you respond?",
    a: "Within 2 hours during business hours. Active clients have a direct WhatsApp line to their account manager.",
  },
  {
    q: "Can I visit the warehouse?",
    a: "Yes. We welcome brand visits to our Shenzhen facility. Let us know in your booking and we\'ll arrange a warehouse tour.",
  },
  {
    q: "Do you work with agencies?",
    a: "Absolutely. We have agency-specific pricing and multi-brand management for logistics agencies managing 3+ brands.",
  },
];

export default function ContactDetails() {
  const staggerContainer = {
    initial: {},
    whileInView: {
      transition: {
        staggerChildren: 0.1
      }
    },
    viewport: { once: true, margin: "-50px" }
  };

  const cardVariant = {
    initial: { opacity: 0, x: -30 },
    whileInView: { opacity: 1, x: 0 },
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
  };

  return (
    <div className="space-y-8">
      {/* Contact cards */}
      <motion.div 
        className="space-y-4"
        variants={staggerContainer}
        initial="initial"
        whileInView="whileInView"
        viewport={{ once: true, margin: "-50px" }}
      >
        {contactInfo.map((info) => (
          <motion.div
            key={info.label}
            variants={cardVariant}
            className="glass-card-dark rounded-3xl p-6 border border-border-dark flex items-start gap-4 hover:border-deep-purple hover:shadow-card transition-all duration-300"
          >
            <div className={`w-11 h-11 rounded-2xl ${info.bg} flex items-center justify-center shrink-0 animate-icon-pulse-glow`}>
              <Icon name={info.icon as any} size={20} className={`${info.color} animate-icon-scale`} />
            </div>
            <div>
              <div className="text-xs font-bold tracking-widest uppercase text-gray-500 mb-1 font-heading">
                {info.label}
              </div>
              <div className="text-sm font-bold text-text-primary font-heading">{info.value}</div>
              <div className="text-xs text-gray-500 mt-0.5">{info.sub}</div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Quick FAQ */}
      <motion.div 
        className="glass-card-dark rounded-3xl p-7 border border-border-dark"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <h4 className="text-sm font-bold font-heading text-text-primary mb-5 uppercase tracking-widest">
          Quick Answers
        </h4>
        <div className="space-y-5">
          {quickFaqs.map((faq) => (
            <div key={faq.q}>
              <div className="flex items-start gap-2 mb-1">
                <Icon name="QuestionMarkCircleIcon" size={16} className="text-link-purple mt-0.5 shrink-0" />
                <span className="text-sm font-semibold text-text-primary font-heading">{faq.q}</span>
              </div>
              <p className="text-sm text-text-secondary leading-relaxed pl-6">{faq.a}</p>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Trust badges */}
      <motion.div 
        className="grid grid-cols-3 gap-3"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.3 }}
      >
        {[
          { icon: "ShieldCheckIcon", text: "Secure & GDPR compliant", color: "text-green-400" },
          { icon: "LockClosedIcon", text: "Your data stays private", color: "text-blue-400" },
          { icon: "StarIcon", text: "4.9/5 client rating", color: "text-yellow-400" },
        ].map((badge) => (
          <div key={badge.text} className="glass-card-dark rounded-2xl p-4 border border-border-dark text-center">
            <Icon name={badge.icon as any} size={20} className={`${badge.color} mx-auto mb-2`} />
            <p className="text-xs text-text-secondary font-medium leading-tight">{badge.text}</p>
          </div>
        ))}
      </motion.div>
    </div>
  );
}